import React, { useCallback, useEffect, useMemo, useState } from "react";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook } from "hooks";
import { useDateFormatSelect } from "store";
import { DealFolderForm, IFile, IFolder } from "store/types";
import { Document } from "store/types/documents";

import { border, cellPx, templateColumns as cols, s } from "../styles";
import { TemplateSelectionReturn } from "../hooks/use-template-selection";

const INDENT = 32;
const headerSx = {
  fontSize: 14,
  fontWeight: 500,
  color: "#071F35",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...cellPx
};
const cellSx = { fontSize: 14, color: "#071F35", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

interface FolderProps {
  folder: IFolder;
  allFolders: IFolder[];
  files: IFile[];
  forms: DealFolderForm[];
  documents: Document[];
  selection: TemplateSelectionReturn;
  investorMap: Map<number, string>;
  level: number;
  forceExpanded?: boolean;
}

const TemplateFolder: React.FC<FolderProps> = ({
  folder,
  allFolders,
  files,
  forms,
  documents,
  selection,
  investorMap,
  level,
  forceExpanded = false
}) => {
  const [expanded, setExpanded] = useState(forceExpanded);
  const formatDate = useFormatDateHook();
  const dateFormat = useDateFormatSelect();

  useEffect(() => {
    if (forceExpanded) setExpanded(true);
  }, [forceExpanded]);

  const childFolders = allFolders.filter((f) => f.parent_id === folder.id);
  const folderFiles = files.filter((f) => f.section_id === folder.id);
  const folderForms = forms.filter((f) => f.section_id === folder.id);
  const folderDocuments = documents.filter((d) => d.section_id === folder.id);

  const visibleFiles = folderFiles.filter((f) => selection.matchesSearch(f.name) && selection.matchesFilters(f));
  const visibleForms = selection.hasActiveFilters ? [] : folderForms.filter((f) => selection.matchesSearch(f.title));
  const visibleDocuments = selection.hasActiveFilters
    ? []
    : folderDocuments.filter((d) => selection.matchesSearch(d.title));
  const visibleChildFolders = childFolders.filter(
    (f) => (!selection.hasActiveFilters && selection.matchesSearch(f.title)) || selection.folderHasMatchingContent(f.id)
  );

  const hasVisibleContent =
    visibleChildFolders.length > 0 || visibleFiles.length > 0 || visibleForms.length > 0 || visibleDocuments.length > 0;

  const isChecked = selection.isSelected("folders", folder.id);
  const isFiltering = !!selection.searchQuery || selection.hasActiveFilters;
  const shouldExpand = expanded;

  const handleToggleExpand = useCallback(() => {
    if (hasVisibleContent) setExpanded((prev) => !prev);
  }, [hasVisibleContent]);

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      selection.toggleFolder(folder.id);
    },
    [folder.id, selection]
  );

  if (isFiltering && !hasVisibleContent) {
    return null;
  }

  return (
    <>
      {/* Folder row */}
      <Stack direction="row" alignItems="center" sx={{ ...s.folderRow, height: 32 }} onClick={handleToggleExpand}>
        <Box
          sx={{
            width: 32 + level * INDENT,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: "8px"
          }}
        >
          <Checkbox checked={isChecked} onClick={handleCheckboxClick} size="small" sx={{ p: 0 }} />
        </Box>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: "text.secondary",
              transition: "transform 0.15s",
              transform: !shouldExpand ? "rotate(-90deg)" : "none",
              visibility: hasVisibleContent ? "visible" : "hidden"
            }}
          />
          <FolderOutlinedIcon sx={{ fontSize: 16, color: "#071F35" }} />
          <MaterialTooltip variant="dark" title={folder.title} placement="top">
            <Typography noWrap sx={{ fontSize: 14, color: "#071F35" }}>
              {folder.title}
            </Typography>
          </MaterialTooltip>
        </Box>
      </Stack>

      {/* Expanded children */}
      {shouldExpand && (
        <>
          {visibleChildFolders.map((child) => (
            <TemplateFolder
              key={child.id}
              folder={child}
              allFolders={allFolders}
              files={files}
              forms={forms}
              documents={documents}
              selection={selection}
              investorMap={investorMap}
              level={level + 1}
              forceExpanded={!!selection.searchQuery}
            />
          ))}
          {visibleFiles.map((file) => {
            const selected = selection.isSelected("files", file.id);
            const investorName = file.investor_id ? investorMap.get(file.investor_id) : null;
            return (
              <Stack
                key={`file-${file.id}`}
                direction="row"
                alignItems="center"
                sx={{ ...(selected ? s.fileRowSelected : s.fileRow), height: 32 }}
                onClick={() => selection.toggleItem("files", { id: file.id, parentId: folder.id })}
              >
                <Box
                  sx={{
                    width: 32 + (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    px: "8px"
                  }}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => selection.toggleItem("files", { id: file.id, parentId: folder.id })}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 380 - (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    ...cellPx,
                    overflow: "hidden"
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: "#071F35", mr: "6px", flexShrink: 0 }} />
                  <MaterialTooltip variant="dark" title={file.name} placement="top">
                    <Typography noWrap sx={cellSx}>
                      {file.name}
                    </Typography>
                  </MaterialTooltip>
                </Box>
                <Box sx={{ ...cols.classification, ...cellPx, overflow: "hidden" }}>
                  {file.document_type ? (
                    <MaterialTooltip variant="dark" title={file.document_type} placement="top">
                      <Typography noWrap sx={cellSx}>
                        {file.document_type}
                      </Typography>
                    </MaterialTooltip>
                  ) : (
                    <Typography sx={{ fontSize: 14, color: "text.disabled", fontStyle: "italic" }}>
                      {"\u2014"}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ ...cols.investor, ...cellPx, overflow: "hidden" }}>
                  {investorName ? (
                    <MaterialTooltip variant="dark" title={investorName} placement="top">
                      <Typography noWrap sx={cellSx}>
                        {investorName}
                      </Typography>
                    </MaterialTooltip>
                  ) : (
                    <Typography sx={{ fontSize: 14, color: "text.disabled" }}>{"\u2014"}</Typography>
                  )}
                </Box>
                <Box sx={{ ...cols.effectiveDate, ...cellPx }}>
                  <MaterialTooltip variant="dark" title={formatDate({ date: file.date, format: dateFormat })} placement="top">
                    <Typography noWrap sx={cellSx}>
                      {formatDate({ date: file.date, format: dateFormat })}
                    </Typography>
                  </MaterialTooltip>
                </Box>
                <Box sx={{ ...cols.uploadDate, ...cellPx }}>
                  <MaterialTooltip variant="dark" title={formatDate({ date: file.created_at, format: dateFormat })} placement="top">
                    <Typography noWrap sx={cellSx}>
                      {formatDate({ date: file.created_at, format: dateFormat })}
                    </Typography>
                  </MaterialTooltip>
                </Box>
              </Stack>
            );
          })}
          {visibleForms.map((form) => {
            const selected = selection.isSelected("forms", form.id);
            return (
              <Stack
                key={`form-${form.id}`}
                direction="row"
                alignItems="center"
                sx={{ ...(selected ? s.fileRowSelected : s.fileRow), height: 32 }}
                onClick={() => selection.toggleItem("forms", { id: form.id, parentId: folder.id })}
              >
                <Box
                  sx={{
                    width: 32 + (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    px: "8px"
                  }}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => selection.toggleItem("forms", { id: form.id, parentId: folder.id })}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 380 - (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    ...cellPx,
                    overflow: "hidden"
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", mr: "6px", flexShrink: 0 }} />
                  <MaterialTooltip variant="dark" title={form.title} placement="top">
                    <Typography noWrap sx={cellSx}>
                      {form.title}
                    </Typography>
                  </MaterialTooltip>
                </Box>
                <Box sx={cols.classification} />
                <Box sx={cols.investor} />
                <Box sx={cols.effectiveDate} />
                <Box sx={cols.uploadDate} />
              </Stack>
            );
          })}
          {visibleDocuments.map((doc) => {
            const selected = selection.isSelected("documents", doc.id);
            return (
              <Stack
                key={`doc-${doc.id}`}
                direction="row"
                alignItems="center"
                sx={{ ...(selected ? s.fileRowSelected : s.fileRow), height: 32 }}
                onClick={() => selection.toggleItem("documents", { id: doc.id, parentId: folder.id })}
              >
                <Box
                  sx={{
                    width: 32 + (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    px: "8px"
                  }}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => selection.toggleItem("documents", { id: doc.id, parentId: folder.id })}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 380 - (level + 1) * INDENT,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    ...cellPx,
                    overflow: "hidden"
                  }}
                >
                  <ArticleOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", mr: "6px", flexShrink: 0 }} />
                  <MaterialTooltip variant="dark" title={doc.title} placement="top">
                    <Typography noWrap sx={cellSx}>
                      {doc.title}
                    </Typography>
                  </MaterialTooltip>
                </Box>
                <Box sx={cols.classification} />
                <Box sx={cols.investor} />
                <Box sx={cols.effectiveDate} />
                <Box sx={cols.uploadDate} />
              </Stack>
            );
          })}
        </>
      )}
    </>
  );
};

interface Props {
  selection: TemplateSelectionReturn;
  investorMap: Map<number, string>;
}

export const TemplateTreeView: React.FC<Props> = ({ selection, investorMap }) => {
  const { parentFolders, folders, files, forms, documents, isSelectedAll, isSomeSelected, toggleAll } = selection;

  const isFiltering = !!selection.searchQuery || selection.hasActiveFilters;

  const visibleParentFolders = useMemo(() => {
    if (!isFiltering) return parentFolders;
    return parentFolders.filter((f) => selection.folderHasMatchingContent(f.id));
  }, [parentFolders, isFiltering, selection]);

  return (
    <Box sx={{ border: border(), borderRadius: "8px", mx: "20px", my: "16px" }}>
      {/* Table header */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.default",
          borderBottom: border(),
          height: 32,
          borderRadius: "8px 8px 0 0"
        }}
      >
        <Box sx={{ ...cols.checkbox, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Checkbox
            checked={isSelectedAll}
            indeterminate={isSomeSelected && !isSelectedAll}
            onChange={toggleAll}
            size="small"
            sx={{ p: 0 }}
          />
        </Box>
        <MaterialTooltip variant="dark" title="Name" placement="top">
          <Typography sx={{ ...cols.name, ...headerSx }}>Name</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Altius Classification" placement="top">
          <Typography sx={{ ...cols.classification, ...headerSx }}>Altius Classification</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Investor" placement="top">
          <Typography sx={{ ...cols.investor, ...headerSx }}>Investor</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Effective date" placement="top">
          <Typography sx={{ ...cols.effectiveDate, ...headerSx }}>Effective date</Typography>
        </MaterialTooltip>
        <MaterialTooltip variant="dark" title="Upload date" placement="top">
          <Typography sx={{ ...cols.uploadDate, ...headerSx }}>Upload date</Typography>
        </MaterialTooltip>
      </Stack>

      {/* Folder tree */}
      {visibleParentFolders.map((folder) => (
        <TemplateFolder
          key={folder.id}
          folder={folder}
          allFolders={folders}
          files={files}
          forms={forms}
          documents={documents}
          selection={selection}
          investorMap={investorMap}
          level={0}
          forceExpanded={isFiltering}
        />
      ))}

      {/* Empty state */}
      {visibleParentFolders.length === 0 && (
        <Box sx={{ py: "56px", px: "20px", textAlign: "center" }}>
          {isFiltering ? (
            <>
              <SearchOffIcon sx={{ fontSize: 32, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                No files match the current filters.
              </Typography>
            </>
          ) : (
            <>
              <FolderOutlinedIcon sx={{ fontSize: 32, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No folders in workspace</Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
